import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  X, UploadCloud, FileText, Loader2, AlertTriangle, CheckCircle2,
  ChevronUp, ChevronDown, Trash2, Plus, AlertCircle, Info, Wallet,
} from 'lucide-react';
import { usePayPreview, fmtPay } from '../hooks/usePayPreview';
import { PayStructureNudge, missingPayDrivers } from './PayStructureNudge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STOP_TYPES = [
  { value: 'pickup', label: 'Pickup' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'cross_dock', label: 'Cross Dock' },
  { value: 'layover', label: 'Layover' },
  { value: 'relay', label: 'Relay' },
];

const APPT_TYPES = [
  { value: 'appt', label: 'APPT (firm appointment)' },
  { value: 'fcfs', label: 'FCFS (first come first served)' },
  { value: 'window', label: 'Window (between times)' },
  { value: 'first_come', label: 'First Come' },
];

const EQUIPMENT_TYPES = [
  { value: 'dry_van', label: 'Dry Van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'step_deck', label: 'Step Deck' },
  { value: 'power_only', label: 'Power Only' },
  { value: 'conestoga', label: 'Conestoga' },
  { value: 'box_truck', label: 'Box Truck' },
  { value: 'hotshot', label: 'Hot Shot' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'other', label: 'Other' },
];

const REEFER_MODES = [
  { value: 'continuous', label: 'Continuous Run' },
  { value: 'start_stop', label: 'Start / Stop' },
  { value: 'protect_from_freeze', label: 'Protect From Freeze' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

/** Color a field based on extraction confidence. */
const confidenceClass = (confidence, hasValue) => {
  if (!hasValue) return 'border-red-300 bg-red-50';
  if (confidence == null) return 'border-slate-200';
  if (confidence >= 0.85) return 'border-green-300 bg-green-50/40';
  if (confidence >= 0.6) return 'border-amber-300 bg-amber-50/40';
  return 'border-red-300 bg-red-50/40';
};

const extractError = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join('; ');
  return err?.message || fallback;
};

const valueOf = (field) => (field && typeof field === 'object' && 'value' in field ? field.value : (field ?? ''));
const confOf = (field) => (field && typeof field === 'object' ? field.confidence : null);

/** Convert the extraction envelope into editable form state. */
const envelopeToForm = (ex) => {
  const flat = {};
  const conf = {};
  const set = (k) => {
    flat[k] = valueOf(ex[k]);
    conf[k] = confOf(ex[k]);
  };
  [
    'load_ref','broker_load_id','confirmation_number','bol_number',
    'broker_name','broker_mc_number','broker_phone','broker_email',
    'broker_contact_name','broker_contact_phone_ext','broker_contact_fax','broker_contact_email',
    'carrier_name','carrier_mc_number','carrier_dot_number','carrier_phone',
    'factoring_company','factoring_address','payment_terms','pod_due_hours',
    'quick_pay_offered','quick_pay_rate',
    'total_rate','equipment_type','equipment_length_ft',
    'reefer_temp_f','reefer_temp_min_f','reefer_temp_max_f','reefer_mode',
    'reefer_precool_required','reefer_special_instructions',
    'commodity','freight_class','nmfc','is_hazmat','hazmat_class','un_number','stackable',
    'seal_numbers','special_instructions',
    'total_weight_lbs','total_pallet_count','total_case_count','total_piece_count','total_cubic_feet',
    'total_miles','deadhead_miles','loaded_miles',
    'tracking_platform','tracking_required','check_call_frequency',
    'exclusive_use_required','no_partials','min_drive_miles_after_pickup','team_driver_required',
    'lumper_handling','pallet_exchange',
  ].forEach(set);
  flat.seal_numbers = Array.isArray(flat.seal_numbers) ? flat.seal_numbers.join(', ') : flat.seal_numbers || '';
  flat.is_hazmat = !!flat.is_hazmat;
  flat.stackable = flat.stackable === '' || flat.stackable == null ? null : !!flat.stackable;
  flat.rate_breakdown = ex.rate_breakdown || [];
  const stops = (ex.stops || []).map((s, i) => {
    const flatStop = { sequence: s.sequence || i + 1, stop_type: s.stop_type || (i === 0 ? 'pickup' : 'delivery') };
    const stopConf = {};
    [
      'facility_name','address','city','state','zip','contact_name','contact_phone',
      'appointment_time','appointment_window_start','appointment_window_end',
      'appointment_type','appointment_number',
      'pickup_number','delivery_number','po_number','confirmation_number','bol_number',
      'shipment_id','warehouse_ref','store_number',
      'weight_lbs','pallet_count','case_count','piece_count','cubic_feet',
      'commodity','notes','instructions','lumper_required','lumper_notes',
    ].forEach((k) => {
      flatStop[k] = valueOf(s[k]);
      stopConf[k] = confOf(s[k]);
    });
    flatStop.references = Array.isArray(s.references) ? s.references : [];
    flatStop.items = Array.isArray(s.items) ? s.items : [];
    flatStop.__confidence = stopConf;
    return flatStop;
  });
  return { fields: flat, confidence: conf, stops };
};

/** Build the body for POST /loads/from-extraction. */
const buildCommitBody = (state, extractionId, overrideDuplicates) => {
  const f = state.fields;
  const toNumber = (v) => {
    if (v === '' || v == null) return null;
    const n = parseFloat(String(v).replace(/,/g, ''));
    return isNaN(n) ? null : n;
  };
  const toInt = (v) => {
    const n = toNumber(v);
    return n == null ? null : Math.round(n);
  };
  const cleanStr = (v) => (typeof v === 'string' ? v.trim() : v) || null;

  const stops = state.stops.map((s, i) => ({
    sequence: i + 1,
    stop_type: s.stop_type,
    facility_name: cleanStr(s.facility_name),
    address: cleanStr(s.address),
    city: cleanStr(s.city),
    state: cleanStr(s.state)?.toUpperCase() || null,
    zip: cleanStr(s.zip),
    contact_name: cleanStr(s.contact_name),
    contact_phone: cleanStr(s.contact_phone),
    appointment_time: cleanStr(s.appointment_time),
    appointment_window_start: cleanStr(s.appointment_window_start),
    appointment_window_end: cleanStr(s.appointment_window_end),
    appointment_type: cleanStr(s.appointment_type),
    appointment_number: cleanStr(s.appointment_number),
    pickup_number: cleanStr(s.pickup_number),
    delivery_number: cleanStr(s.delivery_number),
    po_number: cleanStr(s.po_number),
    confirmation_number: cleanStr(s.confirmation_number),
    bol_number: cleanStr(s.bol_number),
    shipment_id: cleanStr(s.shipment_id),
    warehouse_ref: cleanStr(s.warehouse_ref),
    store_number: cleanStr(s.store_number),
    references: (s.references || []).filter((r) => r.number && String(r.number).trim()),
    items: (s.items || []).filter((it) => it && Object.values(it).some((v) => v !== null && v !== '' && v !== undefined)),
    weight_lbs: toNumber(s.weight_lbs),
    pallet_count: toInt(s.pallet_count),
    case_count: toInt(s.case_count),
    piece_count: toInt(s.piece_count),
    cubic_feet: toNumber(s.cubic_feet),
    commodity: cleanStr(s.commodity),
    notes: cleanStr(s.notes),
    instructions: cleanStr(s.instructions),
    lumper_required: s.lumper_required === null || s.lumper_required === '' ? null : !!s.lumper_required,
    lumper_notes: cleanStr(s.lumper_notes),
  }));

  return {
    extraction_id: extractionId,
    load_ref: cleanStr(f.load_ref),
    broker_load_id: cleanStr(f.broker_load_id),
    confirmation_number: cleanStr(f.confirmation_number),
    bol_number: cleanStr(f.bol_number),
    broker_name: cleanStr(f.broker_name),
    broker_mc_number: cleanStr(f.broker_mc_number),
    broker_phone: cleanStr(f.broker_phone),
    broker_email: cleanStr(f.broker_email),
    broker_contact_name: cleanStr(f.broker_contact_name),
    broker_contact_phone_ext: cleanStr(f.broker_contact_phone_ext),
    broker_contact_fax: cleanStr(f.broker_contact_fax),
    broker_contact_email: cleanStr(f.broker_contact_email),
    carrier_name: cleanStr(f.carrier_name),
    carrier_mc_number: cleanStr(f.carrier_mc_number),
    carrier_dot_number: cleanStr(f.carrier_dot_number),
    carrier_phone: cleanStr(f.carrier_phone),
    factoring_company: cleanStr(f.factoring_company),
    factoring_address: cleanStr(f.factoring_address),
    payment_terms: cleanStr(f.payment_terms),
    pod_due_hours: toInt(f.pod_due_hours),
    quick_pay_offered: f.quick_pay_offered === null || f.quick_pay_offered === '' ? null : !!f.quick_pay_offered,
    quick_pay_rate: cleanStr(f.quick_pay_rate),
    total_rate: toNumber(f.total_rate),
    rate_breakdown: (f.rate_breakdown || []).filter((r) => r && r.description && r.amount != null),
    equipment_type: cleanStr(f.equipment_type),
    equipment_length_ft: toNumber(f.equipment_length_ft),
    reefer_temp_f: toNumber(f.reefer_temp_f),
    reefer_temp_min_f: toNumber(f.reefer_temp_min_f),
    reefer_temp_max_f: toNumber(f.reefer_temp_max_f),
    reefer_mode: cleanStr(f.reefer_mode),
    reefer_precool_required: f.reefer_precool_required === null || f.reefer_precool_required === '' ? null : !!f.reefer_precool_required,
    reefer_special_instructions: cleanStr(f.reefer_special_instructions),
    commodity: cleanStr(f.commodity),
    freight_class: cleanStr(f.freight_class),
    nmfc: cleanStr(f.nmfc),
    is_hazmat: !!f.is_hazmat,
    hazmat_class: cleanStr(f.hazmat_class),
    un_number: cleanStr(f.un_number),
    stackable: f.stackable === null || f.stackable === '' ? null : !!f.stackable,
    seal_numbers: (cleanStr(f.seal_numbers) || '').split(',').map((s) => s.trim()).filter(Boolean) || null,
    special_instructions: cleanStr(f.special_instructions),
    total_weight_lbs: toNumber(f.total_weight_lbs),
    total_pallet_count: toInt(f.total_pallet_count),
    total_case_count: toInt(f.total_case_count),
    total_piece_count: toInt(f.total_piece_count),
    total_cubic_feet: toNumber(f.total_cubic_feet),
    total_miles: toNumber(f.total_miles),
    deadhead_miles: toNumber(f.deadhead_miles),
    loaded_miles: toNumber(f.loaded_miles),
    tracking_platform: cleanStr(f.tracking_platform),
    tracking_required: f.tracking_required === null || f.tracking_required === '' ? null : !!f.tracking_required,
    check_call_frequency: cleanStr(f.check_call_frequency),
    exclusive_use_required: f.exclusive_use_required === null || f.exclusive_use_required === '' ? null : !!f.exclusive_use_required,
    no_partials: f.no_partials === null || f.no_partials === '' ? null : !!f.no_partials,
    min_drive_miles_after_pickup: toNumber(f.min_drive_miles_after_pickup),
    team_driver_required: f.team_driver_required === null || f.team_driver_required === '' ? null : !!f.team_driver_required,
    lumper_handling: cleanStr(f.lumper_handling),
    pallet_exchange: cleanStr(f.pallet_exchange),
    stops,
    override_duplicates: overrideDuplicates,
  };
};


// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

const Field = ({ label, value, confidence, onChange, type = 'text', placeholder, options, required, dataTestid }) => {
  const hasValue = value !== '' && value != null;
  const klass = `w-full px-3 py-2 border rounded-md text-sm transition-colors ${confidenceClass(confidence, hasValue || !required)}`;
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {label} {required && <span className="text-red-500">*</span>}
        {confidence != null && (
          <span className="ml-1 normal-case font-normal text-slate-400">({Math.round(confidence * 100)}%)</span>
        )}
      </span>
      {options ? (
        <select data-testid={dataTestid} value={value || ''} onChange={(e) => onChange(e.target.value)} className={klass}>
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          data-testid={dataTestid}
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={klass}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        />
      )}
    </label>
  );
};

const StopCard = ({ stop, index, total, onChange, onMove, onRemove }) => {
  const [open, setOpen] = useState(true);
  const c = stop.__confidence || {};
  const headerBg = {
    pickup: 'bg-blue-50 border-blue-200',
    delivery: 'bg-green-50 border-green-200',
    cross_dock: 'bg-purple-50 border-purple-200',
    layover: 'bg-amber-50 border-amber-200',
    relay: 'bg-slate-100 border-slate-300',
  }[stop.stop_type] || 'bg-slate-50 border-slate-200';

  return (
    <div className={`border rounded-md ${headerBg}`} data-testid={`stop-card-${index}`}>
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="px-2 py-0.5 rounded-full bg-white text-xs font-bold text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            #{index + 1}
          </span>
          <select
            data-testid={`stop-type-${index}`}
            value={stop.stop_type}
            onChange={(e) => onChange({ ...stop, stop_type: e.target.value })}
            className="bg-white border border-slate-200 text-xs font-semibold rounded px-2 py-1"
          >
            {STOP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <span className="text-sm font-medium text-slate-800 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {stop.facility_name || `${stop.city || ''}${stop.city && stop.state ? ', ' : ''}${stop.state || ''}` || '(blank stop)'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="p-1 disabled:opacity-30" aria-label="Move up">
            <ChevronUp className="w-4 h-4" />
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(1)} className="p-1 disabled:opacity-30" aria-label="Move down">
            <ChevronDown className="w-4 h-4" />
          </button>
          <button type="button" onClick={onRemove} className="p-1 text-red-600 hover:text-red-800" aria-label="Remove stop">
            <Trash2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setOpen(!open)} className="p-1" aria-label="Toggle">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="bg-white p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Facility name" value={stop.facility_name} confidence={c.facility_name}
                 onChange={(v) => onChange({ ...stop, facility_name: v })} dataTestid={`stop-${index}-facility`} />
          <Field label="Address" value={stop.address} confidence={c.address}
                 onChange={(v) => onChange({ ...stop, address: v })} dataTestid={`stop-${index}-address`} />
          <Field label="City" required value={stop.city} confidence={c.city}
                 onChange={(v) => onChange({ ...stop, city: v })} dataTestid={`stop-${index}-city`} />
          <Field label="State" required value={stop.state} confidence={c.state}
                 onChange={(v) => onChange({ ...stop, state: v })} options={US_STATES.map((s) => ({ value: s, label: s }))}
                 dataTestid={`stop-${index}-state`} />
          <Field label="ZIP" value={stop.zip} confidence={c.zip}
                 onChange={(v) => onChange({ ...stop, zip: v })} dataTestid={`stop-${index}-zip`} />
          <Field label="Contact name" value={stop.contact_name} confidence={c.contact_name}
                 onChange={(v) => onChange({ ...stop, contact_name: v })} />
          <Field label="Contact phone" value={stop.contact_phone} confidence={c.contact_phone}
                 onChange={(v) => onChange({ ...stop, contact_phone: v })} />
          <Field label="Appointment time" value={stop.appointment_time} confidence={c.appointment_time}
                 onChange={(v) => onChange({ ...stop, appointment_time: v })} placeholder="YYYY-MM-DDTHH:MM"
                 dataTestid={`stop-${index}-appt`} />
          <Field label="Appointment type" value={stop.appointment_type} confidence={c.appointment_type}
                 onChange={(v) => onChange({ ...stop, appointment_type: v })} options={APPT_TYPES}
                 dataTestid={`stop-${index}-appt-type`} />
          <Field label="Window start" value={stop.appointment_window_start} confidence={c.appointment_window_start}
                 onChange={(v) => onChange({ ...stop, appointment_window_start: v })} placeholder="YYYY-MM-DDTHH:MM" />
          <Field label="Window end" value={stop.appointment_window_end} confidence={c.appointment_window_end}
                 onChange={(v) => onChange({ ...stop, appointment_window_end: v })} placeholder="YYYY-MM-DDTHH:MM" />
          <Field label="Appointment #" value={stop.appointment_number} confidence={c.appointment_number}
                 onChange={(v) => onChange({ ...stop, appointment_number: v })} />
          <Field label="Store #" value={stop.store_number} confidence={c.store_number}
                 onChange={(v) => onChange({ ...stop, store_number: v })} />
          <Field label="Pickup #" value={stop.pickup_number} confidence={c.pickup_number}
                 onChange={(v) => onChange({ ...stop, pickup_number: v })} />
          <Field label="Delivery #" value={stop.delivery_number} confidence={c.delivery_number}
                 onChange={(v) => onChange({ ...stop, delivery_number: v })} />
          <Field label="PO #" value={stop.po_number} confidence={c.po_number}
                 onChange={(v) => onChange({ ...stop, po_number: v })} />
          <Field label="Confirmation #" value={stop.confirmation_number} confidence={c.confirmation_number}
                 onChange={(v) => onChange({ ...stop, confirmation_number: v })} />
          <Field label="BOL #" value={stop.bol_number} confidence={c.bol_number}
                 onChange={(v) => onChange({ ...stop, bol_number: v })} />
          <Field label="Shipment ID" value={stop.shipment_id} confidence={c.shipment_id}
                 onChange={(v) => onChange({ ...stop, shipment_id: v })} />
          <Field label="Warehouse ref" value={stop.warehouse_ref} confidence={c.warehouse_ref}
                 onChange={(v) => onChange({ ...stop, warehouse_ref: v })} />
          <Field label="Weight (lbs)" type="number" value={stop.weight_lbs} confidence={c.weight_lbs}
                 onChange={(v) => onChange({ ...stop, weight_lbs: v })} />
          <Field label="Pallet count" type="number" value={stop.pallet_count} confidence={c.pallet_count}
                 onChange={(v) => onChange({ ...stop, pallet_count: v })} />
          <Field label="Case count" type="number" value={stop.case_count} confidence={c.case_count}
                 onChange={(v) => onChange({ ...stop, case_count: v })} />
          <Field label="Piece count" type="number" value={stop.piece_count} confidence={c.piece_count}
                 onChange={(v) => onChange({ ...stop, piece_count: v })} />
          <Field label="Cubic feet" type="number" value={stop.cubic_feet} confidence={c.cubic_feet}
                 onChange={(v) => onChange({ ...stop, cubic_feet: v })} />
          <Field label="Commodity" value={stop.commodity} confidence={c.commodity}
                 onChange={(v) => onChange({ ...stop, commodity: v })} />

          {/* Additional reference numbers (e.g. multiple POs at one pickup) */}
          {(stop.references || []).length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Additional references ({stop.references.length})
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid={`stop-${index}-refs`}>
                {stop.references.map((r, ri) => (
                  <span key={ri} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-xs font-medium text-slate-700 border border-slate-200">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">{r.kind}</span>
                    {r.number}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Line items per stop */}
          {(stop.items || []).length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Line items ({stop.items.length})
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-2" data-testid={`stop-${index}-items`}>
                <table className="w-full text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead><tr className="text-left text-slate-500"><th className="py-1">PO #</th><th>Qty</th><th>Unit</th><th>Weight</th><th>Cu ft</th><th>Commodity</th></tr></thead>
                  <tbody>
                    {stop.items.map((it, ii) => (
                      <tr key={ii} className="border-t border-slate-100">
                        <td className="py-1 font-mono">{it.po_number || it.sku || '—'}</td>
                        <td>{it.quantity != null ? Number(it.quantity).toLocaleString() : '—'}</td>
                        <td className="text-slate-500">{it.unit || '—'}</td>
                        <td>{it.weight_lbs != null ? `${Number(it.weight_lbs).toLocaleString()} lb` : '—'}</td>
                        <td>{it.cubic_feet != null ? `${Number(it.cubic_feet).toLocaleString()}` : '—'}</td>
                        <td className="truncate">{it.commodity || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <label className="block sm:col-span-2">
            <span className="block text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Notes</span>
            <textarea rows="2" value={stop.notes || ''}
                      onChange={(e) => onChange({ ...stop, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Duplicate match card — expandable side-by-side comparison + 2 actions
// ---------------------------------------------------------------------------

const DiffRow = ({ label, oldVal, newVal }) => {
  const same = String(oldVal ?? '') === String(newVal ?? '');
  const empty = (v) => v === null || v === undefined || v === '';
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-1.5 px-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">{label}</td>
      <td className={`py-1.5 px-2 text-xs ${same ? 'text-slate-600' : 'text-slate-900 bg-amber-50'}`}>
        {empty(oldVal) ? <span className="text-slate-300">—</span> : String(oldVal)}
      </td>
      <td className={`py-1.5 px-2 text-xs ${same ? 'text-slate-600' : 'text-green-900 bg-green-50 font-semibold'}`}>
        {empty(newVal) ? <span className="text-slate-300">—</span> : String(newVal)}
      </td>
    </tr>
  );
};

const DuplicateMatchCard = ({ match, formFields, onMerge, onCancelChoice, submitting }) => {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !details) {
      setLoadingDetails(true);
      try {
        const res = await axios.get(`${API}/loads/${match.id}`, { withCredentials: true });
        setDetails(res.data);
      } catch (e) {
        setDetails({ __error: 'Failed to load' });
      } finally {
        setLoadingDetails(false);
      }
    }
    setExpanded(!expanded);
  };

  const f = formFields || {};
  const fmtMoney = (v) => (v == null || v === '' ? null : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const route = (d) => `${d?.pickup_location || '—'} → ${d?.delivery_location || '—'}`;

  return (
    <div className="bg-white border border-amber-300 rounded-md p-3" data-testid={`duplicate-${match.id}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-900">
            Load {match.load_ref || match.id.slice(-6)}
            <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{match.status}</span>
          </p>
          <p className="text-xs text-slate-600 mt-0.5">{match.pickup_location} → {match.delivery_location}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
            {match.confirmation_number && <span>Conf: {match.confirmation_number}</span>}
            {match.bol_number && <span>BOL: {match.bol_number}</span>}
            {match.broker_load_id && <span>Broker ID: {match.broker_load_id}</span>}
            {match.created_at && <span>Created: {String(match.created_at).slice(0, 10)}</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={handleExpand}
          data-testid={`compare-toggle-${match.id}`}
          className="text-xs font-semibold text-[#002FA7] hover:text-[#00247D] whitespace-nowrap"
        >
          {expanded ? 'Hide compare' : 'View & compare'}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          {loadingDetails && <p className="text-xs text-slate-500">Loading existing load…</p>}
          {!loadingDetails && details?.__error && <p className="text-xs text-red-600">{details.__error}</p>}
          {!loadingDetails && details && !details.__error && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={`compare-table-${match.id}`}>
                <thead>
                  <tr className="bg-slate-100">
                    <th className="py-1 px-2 text-left text-[10px] uppercase tracking-wider text-slate-600">Field</th>
                    <th className="py-1 px-2 text-left text-[10px] uppercase tracking-wider text-slate-600">Existing</th>
                    <th className="py-1 px-2 text-left text-[10px] uppercase tracking-wider text-green-700">New (this upload)</th>
                  </tr>
                </thead>
                <tbody>
                  <DiffRow label="Load ref" oldVal={details.load_ref} newVal={f.load_ref} />
                  <DiffRow label="Broker ID" oldVal={details.broker_load_id} newVal={f.broker_load_id} />
                  <DiffRow label="Confirmation #" oldVal={details.confirmation_number} newVal={f.confirmation_number} />
                  <DiffRow label="BOL #" oldVal={details.bol_number} newVal={f.bol_number} />
                  <DiffRow label="Broker" oldVal={details.broker_name} newVal={f.broker_name} />
                  <DiffRow label="Carrier" oldVal={details.carrier_name} newVal={f.carrier_name} />
                  <DiffRow label="Route" oldVal={route(details)} newVal={route({pickup_location: f._first_pickup, delivery_location: f._last_delivery})} />
                  <DiffRow label="Equipment" oldVal={details.equipment_type} newVal={f.equipment_type} />
                  <DiffRow label="Reefer" oldVal={details.reefer_temp_f != null ? `${details.reefer_temp_f}°F` : null} newVal={f.reefer_temp_f != null && f.reefer_temp_f !== '' ? `${f.reefer_temp_f}°F` : null} />
                  <DiffRow label="Commodity" oldVal={details.commodity} newVal={f.commodity} />
                  <DiffRow label="Total wt" oldVal={details.total_weight_lbs ? `${Number(details.total_weight_lbs).toLocaleString()} lbs` : null} newVal={f.total_weight_lbs ? `${Number(f.total_weight_lbs).toLocaleString()} lbs` : null} />
                  <DiffRow label="Total rate" oldVal={fmtMoney(details.total_rate)} newVal={fmtMoney(f.total_rate)} />
                  <DiffRow label="Stops" oldVal={(details.stops || []).length} newVal={(f._stop_count || 0)} />
                  <DiffRow label="Status" oldVal={details.status} newVal="(unchanged)" />
                </tbody>
              </table>
              <p className="text-[11px] text-slate-500 mt-2">
                Merging will <span className="font-semibold text-amber-700">overwrite</span> the existing load with the highlighted new values (stop list is fully replaced). Status, history, driver assignment, and POD records are preserved.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          onClick={onCancelChoice}
          className="px-3 py-1.5 text-xs font-semibold text-slate-700 rounded-md hover:bg-slate-100"
          data-testid={`cancel-merge-${match.id}`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onMerge(match.id)}
          disabled={submitting}
          data-testid={`merge-into-${match.id}`}
          className="px-3 py-1.5 text-xs font-bold text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-all disabled:opacity-50"
        >
          {submitting ? 'Merging…' : 'Merge into this load'}
        </button>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

const LoadReviewModal = ({ open, onClose, onCreated, viewLoadId, initialExtraction = null, drivers = [] }) => {
  const viewerMode = !!viewLoadId;
  const [stage, setStage] = useState('upload'); // upload | extracting | review | duplicate | submitting | loading-existing
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fileIsImage, setFileIsImage] = useState(false);
  const [assignment, setAssignment] = useState({ driver_id: '', status: 'unassigned', delivered_date: '' });
  const [extractionId, setExtractionId] = useState(null);
  const [extraction, setExtraction] = useState(null); // raw envelope
  const [state, setState] = useState({ fields: {}, confidence: {}, stops: [] });
  const [duplicates, setDuplicates] = useState([]);
  const [error, setError] = useState('');
  const [model, setModel] = useState('');
  const [extractMs, setExtractMs] = useState(null);
  const [asyncJob, setAsyncJob] = useState(null); // {id, status, stage, progress} when using background path
  const [quota, setQuota] = useState(null); // {limit, used, remaining, reset_at, exempt}
  const asyncWsRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!open) {
      setStage('upload'); setPdfFile(null); setPdfUrl(null); setFileIsImage(false);
      setAssignment({ driver_id: '', status: 'unassigned', delivered_date: '' });
      setExtractionId(null); setExtraction(null);
      setState({ fields: {}, confidence: {}, stops: [] });
      setDuplicates([]); setError('');
      setAsyncJob(null);
      setQuota(null);
      try { asyncWsRef.current?.close(); } catch { /* */ }
      asyncWsRef.current = null;
    } else if (!viewLoadId) {
      if (initialExtraction?.extraction) {
        // Pre-extracted (e.g. Tender Inbox) — jump straight to review and show the stored PDF.
        setTimeout(() => applyExtractionResult(initialExtraction), 0);
        if (initialExtraction.extraction_id) {
          axios.get(`${API}/admin/pdf-extractions/${initialExtraction.extraction_id}/file`, { withCredentials: true, responseType: 'blob' })
            .then((r) => { setFileIsImage((r.data.type || '').startsWith('image/')); setPdfUrl(URL.createObjectURL(r.data)); }).catch(() => {});
        }
      }
      // Only show quota when uploading a new PDF (not when viewing an existing one).
      (async () => {
        try {
          const res = await axios.get(`${API}/loads/extract-pdf-v2/quota`, { withCredentials: true });
          setQuota(res.data);
        } catch { /* non-fatal */ }
      })();
    }
  }, [open, viewLoadId]);

  // Viewer mode — fetch the extraction by load id and the stored PDF
  useEffect(() => {
    if (!open || !viewLoadId) return;
    let cancelled = false;
    setStage('loading-existing');
    (async () => {
      try {
        const res = await axios.get(`${API}/loads/${viewLoadId}/extraction`, { withCredentials: true });
        if (cancelled) return;
        const ex = res.data?.extraction || {};
        setExtraction(ex);
        setExtractionId(res.data.id);
        setState(envelopeToForm(ex));
        setPdfFile({ name: res.data.file_name || 'rate_confirmation.pdf' });
        if (res.data.has_stored_file) {
          const fileRes = await axios.get(`${API}/admin/pdf-extractions/${res.data.id}/file`, {
            withCredentials: true, responseType: 'blob',
          });
          if (cancelled) return;
          setFileIsImage((fileRes.data.type || '').startsWith('image/'));
          setPdfUrl(URL.createObjectURL(fileRes.data));
        }
        setStage('review');
      } catch (err) {
        if (cancelled) return;
        setError(extractError(err, 'Failed to load extraction.'));
        setStage('upload');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewLoadId]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  // Async path size threshold — small PDFs stay on the snappy sync endpoint.
  const ASYNC_THRESHOLD_BYTES = 2 * 1024 * 1024; // 2MB

  const applyExtractionResult = (data) => {
    const ex = data?.extraction || {};
    setExtraction(ex);
    setExtractionId(data.extraction_id);
    setDuplicates(data.duplicate_warnings || []);
    setModel(data.model || '');
    setExtractMs(data.duration_ms || null);
    setState(envelopeToForm(ex));
    setStage('review');
  };

  const buildAsyncWsUrl = (jobId) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    let u; try { u = new URL(base); } catch { u = new URL(window.location.origin); }
    const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${u.host}/api/loads/extract-pdf-v2/async/jobs/${jobId}/ws`.replace('/async/jobs/', '/jobs/');
  };

  const subscribeAsyncJob = (jobId) => {
    // WebSocket primary, polling fallback so we don't hang if WS gets blocked.
    let resolved = false;
    let pollTimer = null;
    let ws = null;

    const finish = (data) => {
      if (resolved) return;
      resolved = true;
      try { ws?.close(); } catch { /* */ }
      asyncWsRef.current = null;
      if (pollTimer) clearInterval(pollTimer);
      if (data.status === 'ok' && data.result) {
        applyExtractionResult(data.result);
      } else if (data.status === 'error') {
        setError(data.error || 'PDF extraction failed.');
        setStage('upload');
      }
    };

    try {
      ws = new WebSocket(buildAsyncWsUrl(jobId));
      asyncWsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (payload.type === 'job' && payload.data) {
            setAsyncJob(payload.data);
            if (payload.data.status === 'ok' || payload.data.status === 'error') {
              finish(payload.data);
            }
          }
        } catch { /* */ }
      };
      ws.onerror = () => { /* fall back to polling */ };
    } catch { /* fall back to polling */ }

    pollTimer = setInterval(async () => {
      if (resolved) return;
      try {
        const { data } = await axios.get(`${API}/loads/extract-pdf-v2/jobs/${jobId}`, { withCredentials: true });
        setAsyncJob(data);
        if (data.status === 'ok' || data.status === 'error') finish(data);
      } catch { /* keep polling */ }
    }, 2500);
  };

  const onPickFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isImage = ['png', 'jpg', 'jpeg', 'webp'].includes(ext) || (file.type || '').startsWith('image/');
    if (ext !== 'pdf' && !isImage) {
      setError('Please drop a PDF, JPG or PNG file.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('File is too large (max 15MB).');
      return;
    }
    setError('');
    setPdfFile(file);
    setFileIsImage(isImage);
    setPdfUrl(URL.createObjectURL(file));
    setStage('extracting');
    setAsyncJob(null);

    const useAsync = file.size > ASYNC_THRESHOLD_BYTES;

    try {
      const fd = new FormData();
      fd.append('file', file);
      if (useAsync) {
        const res = await axios.post(`${API}/loads/extract-pdf-v2/async`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAsyncJob(res.data);
        subscribeAsyncJob(res.data.id);
      } else {
        const res = await axios.post(`${API}/loads/extract-pdf-v2`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        applyExtractionResult(res.data);
      }
    } catch (err) {
      setError(extractError(err, 'Failed to extract PDF.'));
      setStage('upload');
    }
  };

  // Drag-and-drop handlers
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return undefined;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    const enter = (e) => { stop(e); el.classList.add('ring-2', 'ring-[#002FA7]', 'bg-blue-50'); };
    const leave = (e) => { stop(e); el.classList.remove('ring-2', 'ring-[#002FA7]', 'bg-blue-50'); };
    const drop = (e) => {
      stop(e);
      el.classList.remove('ring-2', 'ring-[#002FA7]', 'bg-blue-50');
      const file = e.dataTransfer.files?.[0];
      if (file) onPickFile(file);
    };
    el.addEventListener('dragover', stop);
    el.addEventListener('dragenter', enter);
    el.addEventListener('dragleave', leave);
    el.addEventListener('drop', drop);
    return () => {
      el.removeEventListener('dragover', stop);
      el.removeEventListener('dragenter', enter);
      el.removeEventListener('dragleave', leave);
      el.removeEventListener('drop', drop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Helpers
  const setField = (key, value) => setState((s) => ({ ...s, fields: { ...s.fields, [key]: value } }));
  const setStop = (idx, next) => setState((s) => {
    const stops = s.stops.slice();
    stops[idx] = next;
    return { ...s, stops };
  });
  const addStop = () => setState((s) => ({
    ...s,
    stops: [...s.stops, { sequence: s.stops.length + 1, stop_type: 'delivery', city: '', state: '' }],
  }));
  const removeStop = (idx) => setState((s) => ({ ...s, stops: s.stops.filter((_, i) => i !== idx) }));
  const moveStop = (idx, dir) => setState((s) => {
    const stops = s.stops.slice();
    const next = idx + dir;
    if (next < 0 || next >= stops.length) return s;
    [stops[idx], stops[next]] = [stops[next], stops[idx]];
    return { ...s, stops };
  });

  // Submit
  const handleCommit = async (mode = 'create', targetLoadId = null) => {
    setError('');
    const body = buildCommitBody(state, extractionId, mode === 'override');
    body.driver_id = assignment.driver_id || null;
    body.status = assignment.status === 'unassigned' ? null : assignment.status;
    body.delivered_date = assignment.status === 'delivered' ? (assignment.delivered_date || null) : null;
    if (mode === 'merge') {
      body.merge_into_load_id = targetLoadId;
      body.override_duplicates = false;
    }
    // Quick client-side checks
    const pickups = state.stops.filter((s) => s.stop_type === 'pickup');
    const deliveries = state.stops.filter((s) => s.stop_type === 'delivery');
    if (!pickups.length || !deliveries.length) {
      setError('You need at least one pickup AND one delivery stop.');
      return;
    }
    if (!body.equipment_type) { setError('Equipment type is required.'); return; }
    if (!body.total_weight_lbs) { setError('Total weight (lbs) is required.'); return; }
    if (!body.broker_name) { setError('Broker name is required.'); return; }
    if (assignment.status !== 'unassigned' && !assignment.driver_id) { setError('Pick a driver, or set status to Unassigned.'); return; }
    for (const s of body.stops) {
      if (!s.city || !s.state) { setError(`Stop #${s.sequence} is missing city or state.`); return; }
    }

    setStage('submitting');
    try {
      const res = await axios.post(`${API}/loads/from-extraction`, body, { withCredentials: true });
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.duplicates) {
        setDuplicates(detail.duplicates);
        setStage('duplicate');
        return;
      }
      setError(extractError(err, 'Failed to create load.'));
      setStage('review');
    }
  };

  const stopsSorted = useMemo(
    () => state.stops.map((s, i) => ({ ...s, sequence: i + 1 })),
    [state.stops],
  );

  const payRows = useMemo(() => (
    assignment.driver_id
      ? [{ key: 'rc', driver_id: assignment.driver_id, load_rate: state.fields.total_rate, total_miles: state.fields.total_miles, status: assignment.status }]
      : []
  ), [assignment.driver_id, assignment.status, state.fields.total_rate, state.fields.total_miles]);
  const pay = usePayPreview(payRows, !viewerMode && stage === 'review');
  const payRow = pay.byKey.rc;
  const noPayDrivers = useMemo(() => missingPayDrivers(pay.byKey), [pay.byKey]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="load-review-modal">
      <div className="bg-white w-full max-w-7xl rounded-none sm:rounded-md shadow-2xl flex flex-col max-h-screen sm:max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {viewerMode ? 'Original Rate Confirmation' : (stage === 'upload' ? 'Upload Rate Confirmation' : 'Review Extracted Load')}
            </h2>
            <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {viewerMode
                ? `${pdfFile?.name || 'original PDF'} · parsed by ${model || 'AI'}`
                : (stage === 'review'
                  ? `Verify every field before saving. ${model ? `Model: ${model}` : ''}${extractMs ? ` · ${extractMs} ms` : ''}`
                  : 'Drop a PDF, JPG or PNG rate confirmation — AI will parse all stops, equipment, and references.')}
            </p>
          </div>
          <button data-testid="close-review" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stage: UPLOAD */}
        {stage === 'upload' && (
          <div className="flex-1 p-8 overflow-y-auto">
            <div
              ref={dropRef}
              className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center cursor-pointer transition-all"
              onClick={() => fileInputRef.current?.click()}
              data-testid="pdf-dropzone"
            >
              <UploadCloud className="w-14 h-14 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg font-semibold text-slate-800 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Drag &amp; drop a rate confirmation</p>
              <p className="text-sm text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>or click to browse — PDF, JPG or PNG up to 15MB · Amazon Relay or any broker</p>
              <input ref={fileInputRef} data-testid="pdf-file-input" type="file" accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
                     className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
              <p className="text-xs text-slate-400 mt-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Powered by Gemini 3.1 Pro — multi-stop, multi-page, all reference numbers
              </p>
            </div>
            {error && (
              <div data-testid="upload-error" className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />{error}
              </div>
            )}
            {quota && !quota.exempt && (
              <div
                data-testid="extraction-quota"
                className={`mt-4 text-xs flex items-center justify-between px-3 py-2 rounded-md border ${
                  quota.remaining === 0
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : quota.remaining <= 5
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-slate-50 border-slate-200 text-slate-600'
                }`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <span>
                  <span className="font-semibold">{quota.remaining}</span> of {quota.limit} hourly extractions remaining
                  {quota.remaining === 0 && quota.reset_at && (
                    <span className="ml-1">· resets {new Date(quota.reset_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                </span>
                <span className="text-[10px] uppercase tracking-widest opacity-70">PER COMPANY</span>
              </div>
            )}
          </div>
        )}

        {/* Stage: EXTRACTING / LOADING EXISTING */}
        {(stage === 'extracting' || stage === 'loading-existing') && (
          <div className="flex-1 flex items-center justify-center" data-testid="extracting-state">
            <div className="text-center max-w-sm w-full px-6">
              <Loader2 className="w-12 h-12 text-[#002FA7] mx-auto mb-4 animate-spin" />
              <p className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {stage === 'loading-existing'
                  ? 'Loading original extraction…'
                  : asyncJob
                    ? 'Extracting in background…'
                    : 'Parsing your rate confirmation…'}
              </p>
              <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {stage === 'loading-existing'
                  ? 'Fetching the saved PDF and parsed fields.'
                  : asyncJob
                    ? 'Large PDFs run asynchronously — you can keep this tab open; we\'ll auto-resume the moment it\'s done.'
                    : 'This can take 30-60 seconds for multi-page docs.'}
              </p>
              {asyncJob && (
                <div className="mt-4 w-full" data-testid="async-extract-progress">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#002FA7] transition-all duration-500"
                      style={{ width: `${Math.max(5, Math.min(100, asyncJob.progress || 5))}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {asyncJob.stage || asyncJob.status}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stage: DUPLICATE — full screen rich compare + actions */}
        {stage === 'duplicate' && (
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-3xl mx-auto" data-testid="duplicate-panel">
              <div className="bg-amber-50 border border-amber-300 rounded-md p-4 mb-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                  <h3 className="text-lg font-bold text-amber-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                    {duplicates.length} existing load{duplicates.length > 1 ? 's' : ''} match your reference numbers
                  </h3>
                </div>
                <p className="text-sm text-amber-900 mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Click <span className="font-semibold">View &amp; compare</span> to see a side-by-side diff. You can <span className="font-semibold">Merge</span> the new data into an existing load (overwrites + keeps history), <span className="font-semibold">Create anyway</span> as a new duplicate load, or <span className="font-semibold">Cancel</span>.
                </p>
              </div>
              <div className="space-y-3">
                {duplicates.map((m) => (
                  <DuplicateMatchCard
                    key={m.id}
                    match={m}
                    formFields={{
                      ...state.fields,
                      _first_pickup: (() => { const p = state.stops.find((s) => s.stop_type === 'pickup'); return p ? `${p.city || ''}, ${p.state || ''}` : ''; })(),
                      _last_delivery: (() => { const ds = state.stops.filter((s) => s.stop_type === 'delivery'); const d = ds[ds.length - 1]; return d ? `${d.city || ''}, ${d.state || ''}` : ''; })(),
                      _stop_count: state.stops.length,
                    }}
                    onMerge={(targetId) => handleCommit('merge', targetId)}
                    onCancelChoice={onClose}
                    submitting={stage === 'submitting'}
                  />
                ))}
              </div>

              <div className="mt-5 bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <div className="min-w-0 flex-1 pr-4">
                  <p className="text-sm font-semibold text-slate-800">None of these match?</p>
                  <p className="text-xs text-slate-500">Create a new load anyway (a duplicate will be allowed in your loads list).</p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={onClose} data-testid="duplicate-cancel-all" className="px-3 py-1.5 text-xs font-semibold text-slate-700 rounded-md hover:bg-slate-100">Cancel</button>
                  <button
                    type="button"
                    onClick={() => handleCommit('override')}
                    disabled={stage === 'submitting'}
                    data-testid="duplicate-save-anyway"
                    className="px-3 py-1.5 text-xs font-bold text-white bg-[#002FA7] rounded-md hover:bg-[#00247D] transition-all disabled:opacity-50"
                  >
                    Create new load anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stage: REVIEW / SUBMITTING */}
        {(stage === 'review' || stage === 'submitting') && (
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            {/* Left — PDF preview */}
            <div className="lg:w-1/2 border-b lg:border-b-0 lg:border-r border-slate-200 bg-slate-100 overflow-hidden flex flex-col">
              <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{pdfFile?.name}</span>
              </div>
              <div className="flex-1 overflow-auto">
                {pdfUrl && fileIsImage ? (
                  <img data-testid="image-preview" src={pdfUrl} alt="Rate confirmation" className="w-full h-auto" />
                ) : pdfUrl ? (
                  <iframe data-testid="pdf-iframe" src={pdfUrl} title="Rate confirmation" className="w-full h-full min-h-[400px]" />
                ) : (
                  <div className="p-6 text-sm text-slate-500">No preview available</div>
                )}
              </div>
            </div>

            {/* Right — editable extraction */}
            <div className="lg:w-1/2 overflow-y-auto p-6 space-y-6">
              {!viewerMode && (
                <section className="bg-blue-50/60 border border-blue-100 rounded-md p-4" data-testid="assignment-section">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Assignment &amp; Status</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Driver</span>
                      <select
                        data-testid="review-driver-select"
                        value={assignment.driver_id}
                        onChange={(e) => setAssignment((a) => ({ ...a, driver_id: e.target.value, status: e.target.value && a.status === 'unassigned' ? 'assigned' : a.status }))}
                        className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-2 bg-white"
                      >
                        <option value="">— Unassigned —</option>
                        {(drivers || []).filter((d) => d.status === 'active' && d.user_id).map((d) => (
                          <option key={d.user_id} value={d.user_id}>{d.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Status</span>
                      <select
                        data-testid="review-status-select"
                        value={assignment.status}
                        onChange={(e) => setAssignment((a) => ({ ...a, status: e.target.value }))}
                        className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-2 bg-white"
                      >
                        <option value="unassigned">Unassigned</option>
                        <option value="assigned">Assigned</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered (completed — for payroll)</option>
                      </select>
                    </label>
                    {assignment.status === 'delivered' && (
                      <label className="block">
                        <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Delivered on</span>
                        <input
                          type="date"
                          data-testid="review-delivered-date"
                          value={assignment.delivered_date}
                          onChange={(e) => setAssignment((a) => ({ ...a, delivered_date: e.target.value }))}
                          className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-2 bg-white"
                        />
                        <span className="text-[10px] text-slate-500">Sets the pay period. Blank = last delivery appointment.</span>
                      </label>
                    )}
                  </div>
                  {assignment.status === 'delivered' && assignment.driver_id && (
                    <div className="mt-3 flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900" data-testid="review-pay-preview" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <Wallet className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                      {pay.loading && !payRow ? <span>Estimating driver pay…</span> : payRow ? (
                        <span>
                          Adds <strong data-testid="review-pay-amount">{fmtPay(payRow.estimated_pay)}</strong> to {payRow.driver_name || 'the driver'}'s payroll
                          <span className="text-emerald-700"> · {payRow.basis}</span>
                          {payRow.note && <span className="block text-[10px] text-emerald-700/80">{payRow.note}</span>}
                        </span>
                      ) : <span>Driver pay preview unavailable</span>}
                    </div>
                  )}
                  {assignment.driver_id && noPayDrivers.length > 0 && (
                    <div className="mt-3"><PayStructureNudge drivers={noPayDrivers} compact /></div>
                  )}
                </section>
              )}
              {/* Duplicates banner — opens the full compare panel */}
              {duplicates.length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-md p-3 cursor-pointer hover:bg-amber-100 transition-colors" data-testid="duplicate-inline-warning" onClick={() => setStage('duplicate')}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs flex-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <p className="font-semibold text-amber-900 mb-1">{duplicates.length} existing load{duplicates.length > 1 ? 's' : ''} match your reference numbers — click to compare & merge.</p>
                      <ul className="space-y-0.5 text-amber-900">
                        {duplicates.slice(0, 3).map((d) => (
                          <li key={d.id}>• {d.load_ref || d.id.slice(-6)} — {d.pickup_location} → {d.delivery_location}</li>
                        ))}
                      </ul>
                    </div>
                    <span className="text-xs font-semibold text-amber-700 whitespace-nowrap">Review →</span>
                  </div>
                </div>
              )}

              {/* Section: identifiers */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Identifiers</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Load ref" value={state.fields.load_ref} confidence={state.confidence.load_ref}
                         onChange={(v) => setField('load_ref', v)} dataTestid="field-load_ref" />
                  <Field label="Broker load ID" value={state.fields.broker_load_id} confidence={state.confidence.broker_load_id}
                         onChange={(v) => setField('broker_load_id', v)} />
                  <Field label="Confirmation #" value={state.fields.confirmation_number} confidence={state.confidence.confirmation_number}
                         onChange={(v) => setField('confirmation_number', v)} dataTestid="field-confirmation_number" />
                  <Field label="BOL #" value={state.fields.bol_number} confidence={state.confidence.bol_number}
                         onChange={(v) => setField('bol_number', v)} />
                </div>
              </section>

              {/* Section: broker */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Broker</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field required label="Broker name" value={state.fields.broker_name} confidence={state.confidence.broker_name}
                         onChange={(v) => setField('broker_name', v)} dataTestid="field-broker_name" />
                  <Field label="MC #" value={state.fields.broker_mc_number} confidence={state.confidence.broker_mc_number}
                         onChange={(v) => setField('broker_mc_number', v)} />
                  <Field label="Phone" value={state.fields.broker_phone} confidence={state.confidence.broker_phone}
                         onChange={(v) => setField('broker_phone', v)} />
                  <Field label="Email" value={state.fields.broker_email} confidence={state.confidence.broker_email}
                         onChange={(v) => setField('broker_email', v)} />
                  <Field label="Contact" value={state.fields.broker_contact_name} confidence={state.confidence.broker_contact_name}
                         onChange={(v) => setField('broker_contact_name', v)} />
                  <Field label="Factoring company" value={state.fields.factoring_company} confidence={state.confidence.factoring_company}
                         onChange={(v) => setField('factoring_company', v)} />
                </div>
              </section>

              {/* Section: Carrier (the trucking company on the rate con) */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Carrier</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Carrier name" value={state.fields.carrier_name} confidence={state.confidence.carrier_name}
                         onChange={(v) => setField('carrier_name', v)} dataTestid="field-carrier_name" />
                  <Field label="Carrier phone" value={state.fields.carrier_phone} confidence={state.confidence.carrier_phone}
                         onChange={(v) => setField('carrier_phone', v)} />
                  <Field label="Carrier MC #" value={state.fields.carrier_mc_number} confidence={state.confidence.carrier_mc_number}
                         onChange={(v) => setField('carrier_mc_number', v)} dataTestid="field-carrier_mc" />
                  <Field label="Carrier DOT #" value={state.fields.carrier_dot_number} confidence={state.confidence.carrier_dot_number}
                         onChange={(v) => setField('carrier_dot_number', v)} dataTestid="field-carrier_dot" />
                </div>
              </section>

              {/* Section: Payment & Factoring */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Payment</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Payment terms" value={state.fields.payment_terms} confidence={state.confidence.payment_terms}
                         onChange={(v) => setField('payment_terms', v)} placeholder="e.g. 28DAYS, Net 30" dataTestid="field-payment_terms" />
                  <Field label="POD due (hours)" type="number" value={state.fields.pod_due_hours} confidence={state.confidence.pod_due_hours}
                         onChange={(v) => setField('pod_due_hours', v)} placeholder="e.g. 48" />
                  <Field label="Factoring address" value={state.fields.factoring_address} confidence={state.confidence.factoring_address}
                         onChange={(v) => setField('factoring_address', v)} />
                  <Field label="Quick pay rate" value={state.fields.quick_pay_rate} confidence={state.confidence.quick_pay_rate}
                         onChange={(v) => setField('quick_pay_rate', v)} placeholder='e.g. "5%" or "3% / 7-day"' />
                </div>
                {Array.isArray(state.fields.rate_breakdown) && state.fields.rate_breakdown.length > 0 && (
                  <div className="mt-3" data-testid="rate-breakdown">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Rate Breakdown</p>
                    <table className="w-full text-sm bg-slate-50 border border-slate-200 rounded-md" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <tbody>
                        {state.fields.rate_breakdown.map((r, i) => (
                          <tr key={i} className={i > 0 ? 'border-t border-slate-200' : ''}>
                            <td className="px-3 py-1.5 text-slate-700">{r.description}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-slate-900">${Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Section: Carrier Requirements */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Carrier Requirements</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Tracking platform" value={state.fields.tracking_platform} confidence={state.confidence.tracking_platform}
                         onChange={(v) => setField('tracking_platform', v)} placeholder="MacroPoint, TQL Dashboard, FourKites…" dataTestid="field-tracking_platform" />
                  <Field label="Check-call frequency" value={state.fields.check_call_frequency} confidence={state.confidence.check_call_frequency}
                         onChange={(v) => setField('check_call_frequency', v)} placeholder="e.g. daily prior to 10am" />
                  <Field label="Min drive after pickup (mi)" type="number" value={state.fields.min_drive_miles_after_pickup} confidence={state.confidence.min_drive_miles_after_pickup}
                         onChange={(v) => setField('min_drive_miles_after_pickup', v)} dataTestid="field-min_drive" />
                  <Field label="Lumper handling" value={state.fields.lumper_handling} confidence={state.confidence.lumper_handling}
                         onChange={(v) => setField('lumper_handling', v)} placeholder="None, Driver pay & reimburse…" dataTestid="field-lumper" />
                  <Field label="Pallet exchange" value={state.fields.pallet_exchange} confidence={state.confidence.pallet_exchange}
                         onChange={(v) => setField('pallet_exchange', v)} />
                  <div className="flex flex-col gap-2 text-sm pt-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!state.fields.tracking_required} onChange={(e) => setField('tracking_required', e.target.checked)} />
                      Tracking required
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!state.fields.exclusive_use_required} onChange={(e) => setField('exclusive_use_required', e.target.checked)} />
                      Exclusive use required
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!state.fields.no_partials} onChange={(e) => setField('no_partials', e.target.checked)} />
                      No partials
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!state.fields.team_driver_required} onChange={(e) => setField('team_driver_required', e.target.checked)} />
                      Team driver required
                    </label>
                  </div>
                </div>
              </section>

              {/* Section: equipment */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Equipment</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field required label="Equipment type" value={state.fields.equipment_type} confidence={state.confidence.equipment_type}
                         onChange={(v) => setField('equipment_type', v)} options={EQUIPMENT_TYPES} dataTestid="field-equipment_type" />
                  <Field label="Length (ft)" type="number" value={state.fields.equipment_length_ft} confidence={state.confidence.equipment_length_ft}
                         onChange={(v) => setField('equipment_length_ft', v)} />
                  {state.fields.equipment_type === 'reefer' && (
                    <>
                      <Field label="Reefer temp (°F)" type="number" value={state.fields.reefer_temp_f} confidence={state.confidence.reefer_temp_f}
                             onChange={(v) => setField('reefer_temp_f', v)} dataTestid="field-reefer_temp_f" />
                      <Field label="Reefer mode" value={state.fields.reefer_mode} confidence={state.confidence.reefer_mode}
                             onChange={(v) => setField('reefer_mode', v)} options={REEFER_MODES} dataTestid="field-reefer_mode" />
                      <Field label="Temp min (°F)" type="number" value={state.fields.reefer_temp_min_f} confidence={state.confidence.reefer_temp_min_f}
                             onChange={(v) => setField('reefer_temp_min_f', v)} />
                      <Field label="Temp max (°F)" type="number" value={state.fields.reefer_temp_max_f} confidence={state.confidence.reefer_temp_max_f}
                             onChange={(v) => setField('reefer_temp_max_f', v)} />
                      <Field label="Reefer instructions" value={state.fields.reefer_special_instructions} confidence={state.confidence.reefer_special_instructions}
                             onChange={(v) => setField('reefer_special_instructions', v)} placeholder='e.g. "Must precool to -10F"' dataTestid="field-reefer_instructions" />
                      <label className="flex items-center gap-2 text-sm pt-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <input type="checkbox" checked={!!state.fields.reefer_precool_required} onChange={(e) => setField('reefer_precool_required', e.target.checked)} />
                        Precool required
                      </label>
                    </>
                  )}
                </div>
              </section>

              {/* Section: commodity + hazmat */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Commodity &amp; Hazmat</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Commodity" value={state.fields.commodity} confidence={state.confidence.commodity}
                         onChange={(v) => setField('commodity', v)} />
                  <Field label="Freight class" value={state.fields.freight_class} confidence={state.confidence.freight_class}
                         onChange={(v) => setField('freight_class', v)} />
                  <Field label="NMFC" value={state.fields.nmfc} confidence={state.confidence.nmfc}
                         onChange={(v) => setField('nmfc', v)} />
                  <Field label="Hazmat class" value={state.fields.hazmat_class} confidence={state.confidence.hazmat_class}
                         onChange={(v) => setField('hazmat_class', v)} />
                  <Field label="UN #" value={state.fields.un_number} confidence={state.confidence.un_number}
                         onChange={(v) => setField('un_number', v)} />
                  <Field label="Seal #s (comma sep)" value={state.fields.seal_numbers} confidence={state.confidence.seal_numbers}
                         onChange={(v) => setField('seal_numbers', v)} />
                  <label className="flex items-center gap-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <input type="checkbox" checked={!!state.fields.is_hazmat} onChange={(e) => setField('is_hazmat', e.target.checked)} />
                    Hazmat shipment
                  </label>
                  <label className="flex items-center gap-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <input type="checkbox" checked={!!state.fields.stackable} onChange={(e) => setField('stackable', e.target.checked)} />
                    Stackable
                  </label>
                </div>
              </section>

              {/* Section: totals */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Totals</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field required label="Total weight (lbs)" type="number" value={state.fields.total_weight_lbs} confidence={state.confidence.total_weight_lbs}
                         onChange={(v) => setField('total_weight_lbs', v)} dataTestid="field-total_weight_lbs" />
                  <Field label="Total rate ($)" type="number" value={state.fields.total_rate} confidence={state.confidence.total_rate}
                         onChange={(v) => setField('total_rate', v)} dataTestid="field-total_rate" />
                  <Field label="Total miles" type="number" value={state.fields.total_miles} confidence={state.confidence.total_miles}
                         onChange={(v) => setField('total_miles', v)} />
                  <Field label="Deadhead miles" type="number" value={state.fields.deadhead_miles} confidence={state.confidence.deadhead_miles}
                         onChange={(v) => setField('deadhead_miles', v)} />
                  <Field label="Total pallets" type="number" value={state.fields.total_pallet_count} confidence={state.confidence.total_pallet_count}
                         onChange={(v) => setField('total_pallet_count', v)} />
                  <Field label="Total cases" type="number" value={state.fields.total_case_count} confidence={state.confidence.total_case_count}
                         onChange={(v) => setField('total_case_count', v)} />
                  <Field label="Total pieces" type="number" value={state.fields.total_piece_count} confidence={state.confidence.total_piece_count}
                         onChange={(v) => setField('total_piece_count', v)} />
                  <Field label="Total cubic ft" type="number" value={state.fields.total_cubic_feet} confidence={state.confidence.total_cubic_feet}
                         onChange={(v) => setField('total_cubic_feet', v)} />
                </div>
              </section>

              {/* Section: special instructions */}
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Special instructions</h3>
                <textarea rows="3" value={state.fields.special_instructions || ''}
                          onChange={(e) => setField('special_instructions', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                          placeholder="Team driver, tarp, hazmat handling, layover, detention rules…"
                          data-testid="field-special_instructions" />
              </section>

              {/* Section: STOPS */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                    Stops ({stopsSorted.length})
                  </h3>
                  <button type="button" onClick={addStop} data-testid="add-stop" className="text-xs font-semibold text-[#002FA7] hover:text-[#00247D] flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Add stop
                  </button>
                </div>
                <div className="space-y-2">
                  {stopsSorted.map((s, i) => (
                    <StopCard
                      key={i}
                      stop={s}
                      index={i}
                      total={stopsSorted.length}
                      onChange={(next) => setStop(i, next)}
                      onMove={(dir) => moveStop(i, dir)}
                      onRemove={() => removeStop(i)}
                    />
                  ))}
                </div>
              </section>

              {/* Legend */}
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs flex flex-wrap items-center gap-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300" /> high confidence</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> uncertain — please verify</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> missing or low confidence</span>
                <span className="flex items-center gap-1 text-slate-500 ml-auto"><Info className="w-3 h-3" /> Edit any field before saving.</span>
              </div>

              {error && (
                <div data-testid="review-error" className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-900 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        {(stage === 'review' || stage === 'submitting') && !viewerMode && (
          <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-3 bg-white">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">Cancel</button>
            <button
              data-testid="submit-review"
              onClick={() => handleCommit('create')}
              disabled={stage === 'submitting'}
              className="px-5 py-2 bg-[#002FA7] text-white rounded-md text-sm font-bold hover:bg-[#00247D] transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {stage === 'submitting' ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><CheckCircle2 className="w-4 h-4" /> Create load</>}
            </button>
          </div>
        )}
        {viewerMode && stage === 'review' && (
          <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-end bg-white">
            <button onClick={onClose} data-testid="close-viewer" className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadReviewModal;
